<script setup lang="ts">
import type { ProductWithDisplayTags } from "#shared/types/product-display-tag";
import { normalizeProductWithDisplayTags } from "#shared/types/product-display-tag";
import type { StoreProductBundle } from "#shared/types/product-bundle";
import { useClickAndCollectSalesChannelId } from "~/shared/lib/medusa";
import ProductBundleSlider from "./product-bundle-slider.vue";

const props = defineProps<{
    currentProductId: string;
    bundles: StoreProductBundle[];
}>();

type ProductBundleWithProducts = StoreProductBundle & {
    products: ProductWithDisplayTags[];
};

const PRODUCT_FIELDS =
    "title,handle,description,variants.*,thumbnail,images.url,images.metadata,external_id,categories.*,metadata,options.*,options.values.*,variants.options.*,+variants.inventory_quantity,+variants.calculated_price,+product_display_tags.*";
const PRODUCT_PAGE_SIZE = 100;
const client = useMedusaClient();
const clickAndCollectSalesChannelId = useClickAndCollectSalesChannelId();

const mainProductBundles = computed(() =>
    props.bundles.filter(
        (bundle) => bundle.main_product_id === props.currentProductId,
    ),
);
const productIds = computed(() => [
    ...new Set(
        mainProductBundles.value.flatMap((bundle) =>
            bundle.product_ids.filter(
                (productId) => productId !== bundle.main_product_id,
            ),
        ),
    ),
]);
const productIdsKey = computed(() => productIds.value.join(","));

const { data: products } = await useAsyncData(
    () =>
        `product-bundle-products-${props.currentProductId}-${productIdsKey.value || "empty"}`,
    async (): Promise<ProductWithDisplayTags[]> => {
        if (!productIds.value.length) return [];

        const productPages = await Promise.all(
            Array.from(
                {
                    length: Math.ceil(
                        productIds.value.length / PRODUCT_PAGE_SIZE,
                    ),
                },
                (_, index) =>
                    client.store.product.list({
                        id: productIds.value.slice(
                            index * PRODUCT_PAGE_SIZE,
                            (index + 1) * PRODUCT_PAGE_SIZE,
                        ),
                        limit: PRODUCT_PAGE_SIZE,
                        sales_channel_id: clickAndCollectSalesChannelId,
                        country_code: "ru",
                        fields: PRODUCT_FIELDS,
                    }),
            ),
        );
        return productPages
            .flatMap((page) => page.products ?? [])
            .map(normalizeProductWithDisplayTags);
    },
    {
        watch: [productIdsKey],
        default: () => [],
    },
);

const productBundles = computed<ProductBundleWithProducts[]>(() => {
    const productsById = new Map(
        products.value.map((product) => [product.id, product]),
    );

    return mainProductBundles.value
        .map((bundle) => ({
            ...bundle,
            products: bundle.product_ids
                .filter(
                    (productId) => productId !== bundle.main_product_id,
                )
                .map((productId) => productsById.get(productId))
                .filter(
                    (product): product is ProductWithDisplayTags =>
                        Boolean(product),
                ),
        }))
        .filter((bundle) => bundle.products.length > 0);
});
</script>

<template>
    <div v-if="productBundles.length">
        <ProductBundleSlider
            v-for="bundle in productBundles"
            :key="bundle.id"
            :bundle="bundle"
        />
    </div>
</template>
